import { apiClient } from './client';

interface MatchPlayer { id: string; first_name: string; last_name: string }
interface MatchPairInfo { id: number; player1?: MatchPlayer | null; player2?: MatchPlayer | null }
interface MatchTeamInfo { id: number; name: string }

export interface SeasonMatch {
  id: number;
  round_id: number;
  season_id: number;
  season_name?: string | null;
  status: 'pending' | 'proposal_sent' | 'scheduled' | 'result_pending' | 'played' | 'walkover' | 'bye';
  home_user?: { id: string; first_name: string; last_name: string; elo: number } | null;
  away_user?: { id: string; first_name: string; last_name: string; elo: number } | null;
  home_pair?: MatchPairInfo | null;
  away_pair?: MatchPairInfo | null;
  home_team?: MatchTeamInfo | null;
  away_team?: MatchTeamInfo | null;
  winner_team?: MatchTeamInfo | null;
  court?: { id: number; name: string; address: string; city: string } | null;
  scheduled_at?: string | null;
  proposed_by?: string | null;
  result_submitted_by?: string | null;
  home_sets?: number | null;
  away_sets?: number | null;
  winner_user?: { id: string; first_name: string; last_name: string } | null;
  elo_change_home?: number | null;
  elo_change_away?: number | null;
  is_walkover: boolean;
  created_at: string;
}

export interface SeasonRound {
  id: number;
  round_number: number;
  start_date: string;
  deadline: string;
  status: 'upcoming' | 'active' | 'completed';
  matches: SeasonMatch[];
}

export interface SeasonRegistration {
  id: number;
  user: { id: string; first_name: string; last_name: string; elo: number; level?: string };
  pair?: {
    id: number;
    current_elo?: number;
    player1?: { id: string; first_name: string; last_name: string; elo: number } | null;
    player2?: { id: string; first_name: string; last_name: string; elo: number } | null;
  } | null;
  team?: {
    id: number;
    name: string;
    members?: Array<{ id: string; first_name: string; last_name: string; elo: number }>;
  } | null;
  elo_at_registration: number;
  payment_status: 'pending' | 'confirmed' | 'waived';
  registered_at: string;
  wins: number;
  losses: number;
}

export interface Season {
  id: number;
  name: string;
  season_class: string;
  season_type: string;
  start_date: string;
  end_date: string;
  round_duration_days: number;
  num_rounds: number;
  status: 'open' | 'active' | 'finished' | 'archived';
  max_participants?: number;
  fee?: number;
  elo_min?: number;
  elo_max?: number;
  description?: string;
  region?: string;
  participant_count?: number;
  is_registered?: boolean;
  registrations?: SeasonRegistration[];
  rounds?: SeasonRound[];
  created_at: string;
}

export const seasonsApi = {
  list: (status?: string) => apiClient.get<Season[]>('/api/seasons', { params: status ? { status } : {} }),
  get: (id: number) => apiClient.get<Season>(`/api/seasons/${id}`),
  create: (data: Record<string, unknown>) => apiClient.post<Season>('/api/seasons', data),
  update: (id: number, data: Record<string, unknown>) => apiClient.put(`/api/seasons/${id}`, data),
  delete: (id: number) => apiClient.delete(`/api/seasons/${id}`),
  register: (id: number, pairId?: number, teamId?: number) =>
    apiClient.post(`/api/seasons/${id}/register`,
      pairId ? { pair_id: pairId } : teamId ? { team_id: teamId } : {}),
  withdraw: (id: number) => apiClient.delete(`/api/seasons/${id}/register`),
  activate: (id: number) => apiClient.post(`/api/seasons/${id}/activate`),
  archive: (id: number) => apiClient.post(`/api/seasons/${id}/archive`),

  // Matches
  getMyMatches: () => apiClient.get<SeasonMatch[]>('/api/season-matches/mine'),
  propose: (matchId: number, data: { scheduled_at: string; court_id?: number }) =>
    apiClient.post(`/api/season-matches/${matchId}/propose`, data),
  acceptProposal: (matchId: number) => apiClient.post(`/api/season-matches/${matchId}/accept-proposal`),
  submitResult: (matchId: number, data: { home_sets: number; away_sets: number }) =>
    apiClient.post(`/api/season-matches/${matchId}/result`, data),
  confirmResult: (matchId: number) => apiClient.post(`/api/season-matches/${matchId}/confirm-result`),
  rejectResult: (matchId: number, note?: string) =>
    apiClient.post(`/api/season-matches/${matchId}/reject-result`, { note: note ?? '' }),
  adminResult: (matchId: number, home_sets: number, away_sets: number) =>
    apiClient.post(`/api/season-matches/${matchId}/admin-result`, { home_sets, away_sets }),
  walkover: (matchId: number, walkover_against: string | number) =>
    apiClient.post(`/api/season-matches/${matchId}/walkover`, { walkover_against }),
  closeRound: (seasonId: number) => apiClient.post(`/api/seasons/${seasonId}/close-round`),
  setPayment: (seasonId: number, regId: number, payment_status: string) =>
    apiClient.patch(`/api/seasons/${seasonId}/registrations/${regId}/payment`, { payment_status }),
};

export const rankingApi = {
  list: (seasonClass?: string) => apiClient.get<Array<{ id: string; first_name: string; last_name: string; elo: number; rank: number; class: string; level?: string }>>('/api/ranking', { params: seasonClass ? { season_class: seasonClass } : {} }),
  myHistory: () => apiClient.get<Array<{ id: number; elo_before: number; elo_after: number; change: number; reason: string; created_at: string }>>('/api/ranking/history'),
};
